import { useEffect, useState } from 'react';
import { useAdminEvent } from '@/app/admin/AdminEventContext';
import { analyticsApi } from '@/features/admin/analytics/api/analyticsApi';
import { LiveScanCounter } from '@/features/admin/analytics/components/LiveScanCounter';
import { TopExhibitorsChart } from '@/features/admin/analytics/components/TopExhibitorsChart';
import { Button } from '@/shared/components/Button';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { ApiError } from '@/shared/api/httpClient';
import type { LiveAnalyticsDto } from '@/shared/types/domain';

// Admin Analytics is a desktop monitoring view (typically left open on an
// event-organiser's laptop), not a battery-constrained mobile scanning
// session -- unlike the exhibitor Dashboard (Stage 9, event-driven via
// Dexie live queries), a short poll interval here is the right trade-off
// for a genuinely "live" feel without needing a websocket/SignalR channel.
const POLL_INTERVAL_MS = 8000;

export function AnalyticsPage() {
  const { eventId, isLoading: isEventLoading } = useAdminEvent();
  const [data, setData] = useState<LiveAnalyticsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;

    let cancelled = false;
    const controller = new AbortController();

    function poll() {
      analyticsApi
        .getLive(eventId!, controller.signal)
        .then((result) => {
          if (!cancelled) {
            setData(result);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled && !controller.signal.aborted) {
            setError(err instanceof ApiError ? err.userMessage : 'Could not load analytics.');
          }
        });
    }

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
    };
  }, [eventId]);

  async function handleExport(format: 'csv' | 'xlsx') {
    if (!eventId) return;
    setIsExporting(format);
    setExportError(null);

    try {
      await (format === 'csv' ? analyticsApi.exportCsv(eventId) : analyticsApi.exportXlsx(eventId));
    } catch (err) {
      setExportError(err instanceof ApiError ? err.userMessage : `Could not export ${format.toUpperCase()}.`);
    } finally {
      setIsExporting(null);
    }
  }

  if (isEventLoading) return <Spinner label="Loading" />;
  if (!eventId) return <EmptyState icon="◧" title="No events yet" description="Create an event first." />;

  return (
    <div>
      <div className="admin-page__header">
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>Updates every few seconds</p>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={() => void handleExport('csv')} isLoading={isExporting === 'csv'}>
            Export CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExport('xlsx')} isLoading={isExporting === 'xlsx'}>
            Export Excel
          </Button>
        </div>
      </div>

      {exportError && (
        <div className="error-banner" style={{ marginBottom: 'var(--space-4)' }}>
          {exportError}
        </div>
      )}

      {error && !data && <div className="error-banner">{error}</div>}

      {!data ? (
        <Spinner label="Loading analytics" />
      ) : (
        <>
          <LiveScanCounter value={data.liveScanCount} />

          {data.topExhibitors.length === 0 ? (
            <EmptyState icon="▤" title="No scans yet" description="Top exhibitors will appear here once scanning begins." />
          ) : (
            <>
              <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: 'var(--space-4)' }}>
                Top exhibitors
              </h2>
              <TopExhibitorsChart exhibitors={data.topExhibitors} />
            </>
          )}
        </>
      )}
    </div>
  );
}
